import { ProFormDigit } from '@ant-design/pro-components'
import type React from 'react'
import type { FocusEvent } from 'react'
import { useEffect, useRef } from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { type CommonMergeInput, useMergedFieldProps } from '../utils/mergeFieldProps'

export type DigitFieldProps = React.ComponentProps<typeof ProFormDigit>

type SpinnerStepInfo = {
  emitter?: 'handler' | 'keyboard' | 'wheel' | string
}

type SpinnerFieldProps = {
  mode?: 'input' | 'spinner' | string
  onStep?: (value: number | string | null, info: SpinnerStepInfo) => void
  onFocus?: React.FocusEventHandler<HTMLElement>
}

const SPINNER_CONTROL_SELECTOR =
  '.ant-input-number-handler-wrap, .ant-input-number-handler, .ant-input-number-handler-up, .ant-input-number-handler-down, .ant-input-number-action, .ant-input-number-action-up, .ant-input-number-action-down'
const NUMBER_INPUT_SELECTOR = '.ant-input-number-input'
const NUMBER_INPUT_INTERACTIVE_SELECTOR = '.ant-input-number-input-wrap, .ant-input-number-input'

type LastTouchInteraction = 'spinner' | 'input' | 'other'

export function DigitField(props: DigitFieldProps) {
  const fieldCtx = useFieldContext<number | undefined>()
  // Strip `readonly` from rest so it doesn't leak onto the underlying <input>
  // — React expects camelCase `readOnly`, lowercase triggers a console.error.
  const { formItemProps, fieldProps, proFieldProps, className, readonly: _readonly, ...rest } = props
  const lastTouchInteractionRef = useRef<LastTouchInteraction>('other')
  const shouldSelectOnNextFocusRef = useRef(false)
  const pendingBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  type MergeFieldContext = CommonMergeInput<unknown, unknown, unknown, unknown>['fieldCtx']

  const editable = useEditableFieldMode<number | undefined>()

  const merged = useMergedFieldProps({
    fieldCtx: fieldCtx as MergeFieldContext,
    editable,
    props,
    fieldDefaults: {
      fieldProps: {
        onChange: (value: number | null) => {
          fieldCtx.handleChange(value == null ? undefined : value)
        },
      },
    },
  })

  const spinnerFieldProps = merged.fieldProps as SpinnerFieldProps
  const isSpinnerMode = spinnerFieldProps?.mode === 'spinner'
  const originalOnStep = spinnerFieldProps?.onStep
  const originalOnFocus = spinnerFieldProps?.onFocus

  const clearPendingBlur = () => {
    if (pendingBlurTimeoutRef.current) {
      clearTimeout(pendingBlurTimeoutRef.current)
      pendingBlurTimeoutRef.current = null
    }
  }

  const blurActiveNumberInput = () => {
    const activeElement = document.activeElement
    if (!(activeElement instanceof HTMLInputElement)) {
      return
    }

    if (!activeElement.matches(NUMBER_INPUT_SELECTOR)) {
      return
    }

    if (!wrapperRef.current?.contains(activeElement)) {
      return
    }

    activeElement.blur()
  }

  useEffect(() => {
    return () => {
      if (pendingBlurTimeoutRef.current) {
        clearTimeout(pendingBlurTimeoutRef.current)
        pendingBlurTimeoutRef.current = null
      }
    }
  }, [])

  const digitFieldProps = isSpinnerMode
    ? {
        ...merged.fieldProps,
        onStep: (value: number | string | null, info: SpinnerStepInfo) => {
          originalOnStep?.(value, info)

          if (info.emitter !== 'handler') {
            return
          }

          if (lastTouchInteractionRef.current !== 'spinner') {
            return
          }

          requestAnimationFrame(() => {
            if (lastTouchInteractionRef.current === 'spinner') {
              blurActiveNumberInput()
            }
          })

          clearPendingBlur()
          pendingBlurTimeoutRef.current = setTimeout(() => {
            if (lastTouchInteractionRef.current === 'spinner') {
              blurActiveNumberInput()
            }
          }, 80)
        },
        onFocus: (event: FocusEvent<HTMLElement>) => {
          originalOnFocus?.(event)

          if (!shouldSelectOnNextFocusRef.current) {
            return
          }

          shouldSelectOnNextFocusRef.current = false
          const input =
            event.target instanceof HTMLInputElement
              ? event.target
              : wrapperRef.current?.querySelector<HTMLInputElement>(NUMBER_INPUT_SELECTOR)
          input?.select()
        },
      }
    : merged.fieldProps

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <div
        ref={wrapperRef}
        onPointerDownCapture={event => {
          if (!isSpinnerMode) {
            return
          }

          clearPendingBlur()

          const target = event.target as HTMLElement | null
          const isSpinnerControl = Boolean(target?.closest(SPINNER_CONTROL_SELECTOR))
          const isTouchLikePointer = event.pointerType === 'touch' || event.pointerType === 'pen'
          const isInputArea = Boolean(target?.closest(NUMBER_INPUT_INTERACTIVE_SELECTOR))

          if (isSpinnerControl) {
            lastTouchInteractionRef.current = isTouchLikePointer ? 'spinner' : 'other'
            shouldSelectOnNextFocusRef.current = false
            return
          }

          if (isInputArea) {
            lastTouchInteractionRef.current = isTouchLikePointer ? 'input' : 'other'
            shouldSelectOnNextFocusRef.current = true
            return
          }

          lastTouchInteractionRef.current = 'other'
          shouldSelectOnNextFocusRef.current = false
        }}
      >
        <ProFormDigit
          {...rest}
          label={
            rest.label && (
              <EditableLabel
                mode={editable.mode}
                enterEdit={editable.enterEdit}
                exitRead={editable.exitRead}
                onUndo={editable.undoAndExit}
                canUndo={editable.canUndo}
                showActions={!props.readonly}
              >
                {rest.label}
              </EditableLabel>
            )
          }
          proFieldProps={merged.proFieldProps}
          formItemProps={merged.formItemProps}
          fieldProps={digitFieldProps}
        />
      </div>
    </EditableShell>
  )
}
